<script setup>
import {
  Binoculars,
  CalendarSearch,
  FileSearch,
  FolderSearch,
  UserSearch,
} from 'lucide-vue-next'
import { computed } from 'vue'
import { Button } from '@/components/ui/button'

// Define props with default values using defineProps
const props = defineProps({
  // Title is required, so we define it with type and required flag
  title: {
    type: String,
    required: true,
  },
  // Optional props with their default values
  description: {
    type: String,
    default: '',
  },
  // Icon prop - can be a component or a string
  icon: {
    type: [Object, String],
    default: () => Binoculars,
  },
  // Option to hide the icon
  noIcon: {
    type: Boolean,
    default: false,
  },
  // CTA button props are objects that can contain label, to, and onClick properties
  primaryCta: {
    type: Object,
    default: () => null,
    validator: (value) => {
      if (!value)
        return true
      return typeof value.label === 'string'
    },
  },
  secondaryCta: {
    type: Object,
    default: () => null,
    validator: (value) => {
      if (!value)
        return true
      return typeof value.label === 'string'
    },
  },
})

// Computed property to resolve the icon
const resolvedIcon = computed(() => {
  // Check if icon is a string first
  if (typeof props.icon === 'string') {
    // Map string values to corresponding imported icons
    const iconMap = {
      user: UserSearch,
      calendar: CalendarSearch,
      folder: FolderSearch,
      file: FileSearch,
      extra: FileSearch,
    }

    // Return the mapped icon or default to Binoculars if string doesn't match
    return iconMap[props.icon.toLowerCase()] || Binoculars
  }

  // If icon is not a string (it's a component), return it directly
  return props.icon || Binoculars
})

// Helper function to handle button clicks or navigation
function handleAction(cta) {
  if (cta && typeof cta.onClick === 'function') {
    cta.onClick()
  }
}
</script>

<template>
  <div
    class="flex flex-col items-center justify-center text-center p-8 space-y-4"
  >
    <!-- Icon with purple styling in a lighter purple circle -->
    <div v-if="!noIcon" class="mb-2">
      <div
        class="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center"
      >
        <component :is="resolvedIcon" class="w-8 h-8 text-purple-500" />
      </div>
    </div>

    <!-- Title with semantic heading -->
    <h3 class="text-lg font-semibold text-foreground">
      {{ title }}
    </h3>

    <!-- Description with proper text wrapping -->
    <p v-if="description" class="text-sm text-muted-foreground max-w-sm">
      {{ description }}
    </p>

    <!-- Button container with proper spacing -->
    <div v-if="primaryCta || secondaryCta" class="flex gap-3 mt-2">
      <!-- Primary CTA Button -->
      <template v-if="primaryCta">
        <router-link v-if="primaryCta.to" :to="primaryCta.to">
          <Button>{{ primaryCta.label }}</Button>
        </router-link>
        <Button v-else @click="handleAction(primaryCta)">
          {{ primaryCta.label }}
        </Button>
      </template>

      <!-- Secondary CTA Button -->
      <template v-if="secondaryCta">
        <router-link v-if="secondaryCta.to" :to="secondaryCta.to">
          <Button variant="outline">
            {{ secondaryCta.label }}
          </Button>
        </router-link>
        <Button v-else variant="outline" @click="handleAction(secondaryCta)">
          {{ secondaryCta.label }}
        </Button>
      </template>
    </div>
  </div>
</template>
